import { Button } from "@/components/ui/button";
import { urlFor } from "~/client";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import i18n from "@/i18n";
import { loadLocale } from "@/lib/zod-utils";
import { SharedData } from "@/types";
import { router, usePage } from "@inertiajs/react";
import { Check, ChevronsUpDown, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

const languages: App.Enums.Locale[] = ["en", "es"];

function LanguageSwitcher() {
  const { t } = useTranslation("home_common");
  const { auth } = usePage<SharedData>().props;

  const currentLanguageDisplay: string = i18n.language === "en" ? t("en") : t("es");

  const handleLanguageChange = (language: string) => {
    loadLocale(language);
    if (auth.user && auth.company) {
      if (language === auth.user.locale) {
        return;
      }
      router.patch(
        urlFor("profile.locale.update"),
        { locale: language },
        {
          only: ["auth"],
          preserveScroll: true,
        },
      );
    } else {
      i18n.changeLanguage(language);

      router.put(
        urlFor("set-locale", { locale: language }),
        { locale: language },
        { preserveScroll: true, preserveState: true },
      );
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="focus-visible:ring-primary/20 flex max-w-[75px] items-center !border-slate-300 !bg-white !text-slate-950 hover:!bg-primary/10 hover:!text-primary"
        >
          <Globe className="mr-2 h-4 w-4" />
          <span className="truncate">{currentLanguageDisplay}</span>
          <ChevronsUpDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("languagesLabel", "Languages")}</DropdownMenuLabel>
        <DropdownMenuGroup>
          {languages.map((language) => (
            <DropdownMenuItem
              key={language}
              onSelect={() => handleLanguageChange(language)}
              disabled={language === i18n.language}
              className="flex items-center justify-between"
            >
              <span>
                {t(language)} - {language}
              </span>
              {language === i18n.language && <Check className="ml-2 h-4 w-4" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSwitcher;
